import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReplayIcon from '@mui/icons-material/Replay';

import axios from 'axios';
import { API_PATH } from "../../../App";
import { getAxiosConfig } from "../../../helpers/AuthHelper";

interface FinanceReportAuditDto {
    machinesNotConfigured: Record<string, string[]>;
    totalExpenseUsd: number;
    totalIncome: number;
}

const FinanceReportAudit = () => {
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [report, setReport] = useState<FinanceReportAuditDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const params: any = {};
            if (selectedDate) {
                params.date = selectedDate;
            }
            const response = await axios.get(`${API_PATH}/audit/finance-report`, {
                ...getAxiosConfig(),
                params: params
            });
            setReport(response.data);
        } catch (err: any) {
            console.error(err);
            setError('Failed to fetch finance audit report. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [selectedDate]); // Refetch when date changes

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const { machinesNotConfigured, totalExpenseUsd, totalIncome } = report || { machinesNotConfigured: {}, totalExpenseUsd: 0, totalIncome: 0 };
    const notConfiguredCount = Object.values(machinesNotConfigured).reduce((acc, list) => acc + list.length, 0);

    return (
        <Box p={2}>
            {/* Header with Refresh and Date Picker */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">Finance Report Audit</Typography>
                <Box display="flex" gap={2} alignItems="center">
                    <input
                        type="date"
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '16px'
                        }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        startIcon={<ReplayIcon />}
                        onClick={fetchReport}
                        color="primary"
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderLeft: '6px solid #f44336' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Total Expense (USD)</Typography>
                                    <Typography variant="h4" color="error.main">
                                        ${totalExpenseUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <MonetizationOnIcon sx={{ fontSize: 50, color: '#f44336', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ borderLeft: '6px solid #4caf50' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Total Income</Typography>
                                    <Typography variant="h4" color="success.main">
                                        ${totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <AttachMoneyIcon sx={{ fontSize: 50, color: '#4caf50', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ borderLeft: '6px solid #ff9800', bgcolor: notConfiguredCount > 0 ? '#fff3e0' : 'background.paper' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Unconfigured Machines</Typography>
                                    <Typography variant="h4" color="warning.main">
                                        {notConfiguredCount}
                                    </Typography>
                                </Box>
                                <WarningIcon sx={{ fontSize: 50, color: '#ff9800', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Machines Not Configured Section */}
            <Accordion defaultExpanded={notConfiguredCount > 0} disableGutters elevation={2} sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center">
                        <WarningIcon color="warning" sx={{ mr: 2 }} />
                        <Typography variant="h6" color="textPrimary">
                            Machines Not Configured (No Machine ID)
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell>Prefix Group</TableCell>
                                    <TableCell>Worker IDs</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.keys(machinesNotConfigured).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                            No unconfigured machines found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.entries(machinesNotConfigured).map(([prefix, workers]) => (
                                        <TableRow hover key={prefix}>
                                            <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', width: '20%' }}>
                                                {prefix.toUpperCase()}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={1} flexWrap="wrap">
                                                    {workers.map(w => (
                                                        <Chip key={w} label={w} size="small" variant="outlined" />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

        </Box>
    );
};

export default FinanceReportAudit;
